import { v4 } from "uuid";

import { AnalyticsProductDto } from "@intelligems/ig-types/dtos/analytics-products";
import { AudienceDto } from "@intelligems/ig-types/dtos/audiences";
import {
  buildSurveySummary,
  formatUserInputAnswers,
  SurveySummaryRow,
} from "@intelligems/ig-types/schemas/ai/survey-reply";
import { CheckoutBlockDto } from "@intelligems/ig-types/dtos/checkout-blocks";
import { CreateOnsiteInjectionDto } from "@intelligems/ig-types/dtos/onsite-injections";
import { ExperienceKeyMetricFe } from "@intelligems/ig-types/fe-only/custom-events";
import { ExperiencePageTargetingFe } from "@intelligems/ig-types/fe-only/page-targeting";
import { GenericExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import { OfferDto } from "@intelligems/ig-types/dtos/offers";
import { RedirectDto } from "@intelligems/ig-types/dtos/redirects";
import { TemplateAssetType } from "@intelligems/ig-types/schemas/organization";
import { UiMessage } from "@intelligems/ig-types/schemas/ai/ui-message";
import { UserInput } from "@intelligems/ig-types/schemas/ai/node-execution";
import { VariationFe } from "@intelligems/ig-types/fe-only/variations";

import {
  INITIAL_ASSISTANT_MESSAGE_TEXT,
  INITIAL_TIMESTAMP_OFFSET,
} from "../aiChat.constants";

export const createInitialAssistantMessage = (text?: string): UiMessage => {
  const messageId = v4();
  const timestamp = Date.now() - INITIAL_TIMESTAMP_OFFSET;

  return {
    id: messageId,
    role: "assistant",
    timestamp,
    parts: [
      {
        id: `assistant-initial-${v4()}`,
        type: "text",
        timestamp,
        text: text ?? INITIAL_ASSISTANT_MESSAGE_TEXT,
      },
    ],
  };
};

export const createUserMessage = (
  input: UserInput | undefined
): { message: UiMessage | null; text: string } => {
  if (!input) {
    return { message: null, text: "" };
  }
  const messageId = v4();
  const timestamp = Date.now();

  // `text` is the payload the model reads; `displayText` is what the transcript
  // shows when the two differ. A choice reply is the case that needs both — the
  // agent must receive the option value it listed, the reader should see the
  // label that was on the chip. Kept in step with the server echo that
  // reconciles over this bubble (`executor.service.ts` `addUserInput`).
  let text = "";
  let displayText: string | undefined;
  // Structured recap for an answers reply — the transcript renders it as the
  // "Survey summary" card instead of the joined `displayText` run-on.
  let surveySummary: SurveySummaryRow[] | undefined;
  if (input.type === "text") {
    text = input.content || "";
  } else if (input.type === "choice") {
    text = input.choice || "";
    displayText = input.label;
  } else if (input.type === "multi-choice") {
    text = input.choices.map((option) => option.value).join(", ");
    displayText = input.choices.map((option) => option.label).join(", ");
  } else if (input.type === "confirmation") {
    text = input.confirmed ? "Yes" : "No";
  } else if (input.type === "file") {
    text = input.files.map((file) => file.name).join(", ");
  } else if (input.type === "answers" || input.type === "survey") {
    ({ text, displayText } = formatUserInputAnswers(input.answers));
    surveySummary = buildSurveySummary(input.answers);
  }

  // File parts go BEFORE the text bubble so chips render above the text
  // (ChatGPT-style), matching the order the server emits on the echo. Keeping
  // the two in sync avoids a brief flicker between optimistic render and
  // reconciliation; see executor.service.ts `addUserInput` for the server side.
  const parts: UiMessage["parts"] = [];
  if (input.type === "text" && input.files && input.files.length > 0) {
    for (const file of input.files) {
      parts.push({
        type: "file",
        // `url` is always the durable `s3://` reference — this message ships
        // verbatim to the backend as `initialContext.messages`/continuation
        // input, and a client-only `blob:` URL there makes the AI SDK's
        // file-download step throw. `localPreviewUrl` rides alongside purely
        // for instant local rendering (see `AttachmentTile`) — the server
        // never reads it.
        url: file.url,
        localPreviewUrl: file.localPreviewUrl,
        mediaType: file.mediaType,
        filename: file.name,
        timestamp,
      });
    }
  }
  // Skip the text part entirely when the user submitted file(s) without
  // typing — avoids rendering an empty bubble below the tiles.
  if (text) {
    parts.push({
      id: `user-input-${v4()}`,
      type: "text",
      text,
      ...(displayText && displayText !== text ? { displayText } : {}),
      ...(surveySummary ? { surveySummary } : {}),
      timestamp,
    });
  }

  return {
    message: {
      id: messageId,
      role: "user",
      timestamp,
      parts,
    },
    text,
  };
};

interface BuildWorkflowVariablesParams {
  experience: GenericExperienceFe;
  domain: string | undefined;
  orgId: string | undefined;
  shopId: string | undefined | null;
  variations: VariationFe[];
  redirects: RedirectDto[];
  onsiteInjections: CreateOnsiteInjectionDto[];
  audience: AudienceDto;
  experienceKeyMetrics: ExperienceKeyMetricFe[];
  themeTemplates: TemplateAssetType[];
  pageTargeting: ExperiencePageTargetingFe[];
  analyticsProducts: AnalyticsProductDto[];
  started: boolean;
  /**
   * Offers already configured on this experience, so the agent can see what it
   * built on a previous turn and update rather than duplicate — every other
   * setter's state is resent the same way.
   */
  offers: OfferDto[];
  /**
   * Whether the org can still enable another offer. Computed on the client by
   * `useIsUnderOfferLimit` (org config `max_enabled_offers` vs. the offers
   * already live) and passed through so the agent can say so BEFORE configuring
   * a discount the merchant won't be able to turn on. Deliberately not
   * recomputed server-side: that would duplicate the limit logic across layers.
   */
  isUnderOfferLimit: boolean;
  /**
   * Whether offers and onsite upsells are locked behind an upgrade. Computed
   * from the same billing gate model the rest of the app uses, so the agent
   * can't promise something the workspace will then gate.
   */
  isMerchandisingGated: boolean;
  experienceStartedAt: number | null;
  /**
   * Whether the store's onsite upsells run on Agentic Logic rules (the same
   * per-org toggle the backend gates every Agentic Logic read on). The agent
   * describes the engine the store actually has — legacy per-upsell IF/THEN
   * rules, or a shared rule Viv asks the merchant to pick — never the other.
   */
  isAgenticLogicEnabled: boolean;
  /**
   * Checkout blocks already configured across the experience's variations,
   * so a set-*-block tool can tell whether it's editing an existing block
   * (reuse its real id/locationId) or creating a new one.
   */
  checkoutBlocks: CheckoutBlockDto[];
}

export const buildWorkflowVariables = ({
  experience,
  domain,
  orgId,
  shopId,
  variations,
  redirects,
  onsiteInjections,
  audience,
  experienceKeyMetrics,
  themeTemplates,
  pageTargeting,
  analyticsProducts,
  started,
  isUnderOfferLimit,
  isMerchandisingGated,
  isAgenticLogicEnabled,
  offers,
  experienceStartedAt,
  checkoutBlocks,
}: BuildWorkflowVariablesParams) => {
  return {
    // Sent so server-side sinks (Slack/S3/Mixpanel) can tie a turn back to the
    // experience being configured — `experienceDetails` carries only the
    // human-facing name/description.
    experienceId: experience.id,
    experienceDetails: {
      name: experience.name,
      description: experience.description,
    },
    isContinue: started,
    isUnderOfferLimit,
    isMerchandisingGated,
    isAgenticLogicEnabled,
    offers,
    experienceType: experience.type,
    domain,
    orgId,
    shopId,
    variations,
    redirects,
    css: onsiteInjections.filter((i) => Boolean(i.customCss)),
    js: onsiteInjections.filter((i) => Boolean(i.customJs)),
    audience,
    needs: {
      experienceDetails: { requiresUpdate: true, reasoning: "" },
      variations: { requiresUpdate: true, reasoning: "" },
      urlRedirects: { requiresUpdate: true, reasoning: "" },
      templateRedirects: { requiresUpdate: true, reasoning: "" },
      themeRedirects: { requiresUpdate: true, reasoning: "" },
      pageTargeting: { requiresUpdate: true, reasoning: "" },
      audience: { requiresUpdate: true, reasoning: "" },
      onsiteInjections: { requiresUpdate: true, reasoning: "" },
      keyMetrics: { requiresUpdate: true, reasoning: "." },
    },
    keyMetrics: experienceKeyMetrics,
    // How conversions are counted today (`setAnalyticsViewType`), plus the
    // products scoping it when in `test_orders_only` — so the agent can tell
    // whether the merchant's request actually changes the current setting.
    analyticsViewType:
      experience.experienceAnalysis?.analyticsViewType ?? "sitewide_orders",
    analyticsProducts: analyticsProducts.map((product) => ({
      id: product.id,
      title: product.title,
    })),
    templates: themeTemplates || [],
    pageTargeting,
    testTypes: Object.entries(experience.testTypes)
      .reduce((previousValue: string[], currentValue) => {
        const [key, value] = currentValue;
        if (value) {
          previousValue.push(key);
        }
        return previousValue;
      }, [])
      .join(","),
    experienceStartedAt,
    checkoutBlocks,
  };
};
